import invariant from 'invariant';
import apiRequest from 'lib/apiRequest';
import { clone } from 'lodash';
import Entity from 'lib/Entity';
import Group from 'entities/Group';
import GroupStore from 'groups/stores/GroupStore';
import Organizations from 'collections/Organizations';
import Subscriber from 'entities/Subscriber';
import Subscription from 'entities/Subscription';
import Subscriptions from 'collections/Subscriptions';

const groupIsNotStaffClass = (subscription: Subscription) => {
  const group = GroupStore.getGroup(subscription.getGroupId());
  invariant(group, 'Relationship missing group for subscription');
  // @ts-expect-error ts-migrate(2533) FIXME: Object is possibly 'null' or 'undefined'.
  return !group.isStaffClass();
};

const subscriptionByGroupNameComparator = (a: Subscription, b: Subscription) => {
  const groupA = GroupStore.getGroup(a.getGroupId());
  const groupB = GroupStore.getGroup(b.getGroupId());
  if (!groupA) {
    return -1;
  }
  if (!groupB) {
    return 1;
  }
  return groupA.getName().localeCompare(groupB.getName());
};

class Relationship extends Entity {
  initialize() {
    this.urlRoot = '/relationships';
  }

  parse(response: any) {
    const relationship = clone(response);
    relationship['subscriptions_to_me'] = Subscriptions.fromJSON(
      relationship['subscriptions_to_me']
    );
    relationship['subscriptions_to_user'] = Subscriptions.fromJSON(
      relationship['subscriptions_to_user']
    );
    relationship['related_user'] = Subscriber.fromJSON(relationship['related_user']);
    relationship['id'] = relationship['related_user']['id'];
    relationship['common_organizations_with_user'] = Organizations.fromJSON(
      relationship['common_organizations_with_user']
    );
    return relationship;
  }

  getRelatedUser(): Subscriber {
    return this.get('related_user');
  }

  // ordered by group name
  getSubscriptionsToMe(): Subscriptions {
    return new Subscriptions(
      this.get('subscriptions_to_me')
        .filter(groupIsNotStaffClass)
        .sort(subscriptionByGroupNameComparator)
    );
  }

  getSubscriptionsToMeIncludingStaffClass(): Subscriptions {
    return this.get('subscriptions_to_me');
  }

  // ordered by group name
  getSubscriptionsToUser(): Subscriptions {
    return new Subscriptions(
      this.get('subscriptions_to_user')
        .filter(groupIsNotStaffClass)
        .sort(subscriptionByGroupNameComparator)
    );
  }

  getCommonOrganizationsWithUser() {
    return this.get('common_organizations_with_user');
  }

  getAdminAtOrgIds() {
    return this.get('admin_at_org_ids') || [];
  }

  unsubscribe(subscription: Subscription): void {
    [this.get('subscriptions_to_me'), this.get('subscriptions_to_user')].forEach((collection) => {
      collection.remove(subscription.id);
    });
  }

  unsubscribeFromGroup(group: Group): void {
    const groupId = group.id;
    [this.get('subscriptions_to_me'), this.get('subscriptions_to_user')].forEach((collection) => {
      // @ts-expect-error ts-migrate(7006) FIXME: Parameter 's' implicitly has an 'any' type.
      const subscription = collection.find((s) => s.getGroupId() === groupId);
      if (subscription) {
        collection.remove(subscription);
      }
    });
  }

  hasSubscriptions() {
    return !this.getSubscriptionsToMe().isEmpty() || !this.getSubscriptionsToUser().isEmpty();
  }

  getChatEligibilityState() {
    return this.get('chat_eligibility_state');
  }

  isEligibleForChat() {
    return this.getChatEligibilityState() === 'eligible';
  }

  isPendingForChat() {
    return this.getChatEligibilityState() === 'pending';
  }

  isUnder13() {
    return this.getChatEligibilityState() === 'under_13';
  }

  isUnavailableForChat() {
    return this.getChatEligibilityState() === 'unavailable';
  }

  isAgeUnknown() {
    return this.getChatEligibilityState() === 'unknown';
  }

  getOrgAffiliationViews() {
    return this.get('organization_affiliations');
  }

  static updateName(subscriberId: Backbone$Id, firstName: string, lastName: string) {
    return apiRequest(`/relationships/${subscriberId}`, 'PATCH', {
      related_user: {
        first_name: firstName,
        last_name: lastName,
      },
    }).then((response) => Relationship.fromJSON(response));
  }

  static updateNote(subscriberId: Backbone$Id, note: string) {
    return apiRequest(`/relationships/${subscriberId}`, 'PATCH', {
      related_user: {
        note,
      },
    }).then((response) => Relationship.fromJSON(response));
  }

  static confirmUntrustedName(subscriberId: Backbone$Id) {
    return apiRequest(`/relationships/${subscriberId}`, 'PATCH', {
      related_user: {
        untrusted_name: false,
      },
    }).then((response) => Relationship.fromJSON(response));
  }

  static block(relatedUserId: Backbone$Id) {
    return apiRequest(`/relationships/${relatedUserId}`, 'PATCH', {
      related_user: {
        blocked: true,
      },
    }).then((response) => Relationship.fromJSON(response));
  }

  static unblock(relatedUserId: Backbone$Id) {
    return apiRequest(`/relationships/${relatedUserId}`, 'PATCH', {
      related_user: {
        blocked: false,
      },
    }).then((response) => Relationship.fromJSON(response));
  }
}

export default Relationship;
